import {useShipmentListQuery, useShipmentStatusGetQuery} from "../../app/api/api";
import {ColumnDef, FilterDef, FilterOperator, FilterType, ListRequestIdModelTable} from "../../components/ExTable";

import {useTranslation} from "react-i18next";
import {dateTimeColumnDef, defaultColumnDef} from "../../components/ExTable/variables";
import {ordinaryDetailPageLinkTo} from "../../utils/ordinaryDetailPageLink";
import Page from "../../components/Layout/Page";
import {ShipmentField, ShipmentFlat, ShipmentStatus} from "../../app/api/model/shipment";
import {RoutePath} from "../../types";
import {getTranslationKey, TranslationContext} from "../../utils/getTranslationKey";
import {lowerCase} from "lodash";
import {getStatusLabel} from "../orders/helpers";
import {getDateFilterDefinitions} from "../../utils/getDateFilterDefinitions";

export default function ShipmentList() {
    const {t} = useTranslation();
    const {data: shipmentStatus} = useShipmentStatusGetQuery();

    const columnDefs: ColumnDef<ShipmentFlat>[] = [
        {
            fieldName: ShipmentField.ID,
            label: t("generic_id"),
            width: 80,
            generateLink: ordinaryDetailPageLinkTo(RoutePath.SHIPMENTS),
            ...defaultColumnDef,
            sticky: true,
        },
        {
            ...defaultColumnDef,
            fieldName: ShipmentField.ORDER_NUMBER,
            label: t("order_orderNumber"),
        },
        {
            ...defaultColumnDef,
            fieldName: ShipmentField.SHIPMENT_NUMBER,
            label: t("shipments_shipmentNumber"),
        },
        {
            fieldName: ShipmentField.STATUS,
            align: "center",
            label: t("generic_status"),
            sortable: true,
            formatCellText: ({row}) => getStatusLabel(row.status, t, TranslationContext.SHIPMENT_STATUS),
        },
        {
            ...defaultColumnDef,
            fieldName: ShipmentField.CUSTOMER_NAME,
            label: t("generic_customerName"),
        },
        {
            fieldName: ShipmentField.CREATED_AT,
            label: t("generic_shipmentCreatedAt"),
            ...dateTimeColumnDef,
        },
        {
            fieldName: ShipmentField.CONFIRMED_AT,
            label: t("shipment_confirmedAt"),
            ...dateTimeColumnDef,
        },
        {
            fieldName: ShipmentField.SHIPPED_AT,
            label: t("shipment_shippedAt"),
            ...dateTimeColumnDef,
        },
        {
            fieldName: ShipmentField.DELIVERED_AT,
            label: t("shipment_deliveredAt"),
            ...dateTimeColumnDef,
        },
    ];
    const filterDefs: FilterDef<ShipmentFlat>[] = [
        {
            fieldName: ShipmentField.SHIPMENT_NUMBER,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: ShipmentField.ORDER_NUMBER,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: ShipmentField.CUSTOMER_NAME,
            filterOperators: [FilterOperator.EQ, FilterOperator.CONTAINS, FilterOperator.IN],
        },
        {
            fieldName: ShipmentField.STATUS,
            type: FilterType.ENUM,
            filterOperators: [FilterOperator.EQ, FilterOperator.NOT],
            enumValues: (shipmentStatus || Object.keys(ShipmentStatus)).map((status: string) => {
                return {
                    value: status,
                    labelKey: getTranslationKey({
                        context: TranslationContext.SHIPMENT_STATUS,
                        value: lowerCase(status),
                    }),
                };
            }),
        },
        ...getDateFilterDefinitions<ShipmentFlat>([
            ShipmentField.DELIVERED_AT,
            ShipmentField.SHIPPED_AT,
            ShipmentField.CONFIRMED_AT,
            ShipmentField.CREATED_AT,
            ShipmentField.CANCELED_AT,
        ]),
    ];

    return (
        <Page>
            <ListRequestIdModelTable
                dataTest="shipmentsList"
                queryHook={useShipmentListQuery}
                columnDefs={columnDefs}
                filterDefs={filterDefs}
                title={t("generic_shipments")}
            />
        </Page>
    );
}
